<template>
	<div class="flex flex-col gap-1">
		<p class="flex" :class="isProductPage ? 'text-xl' : 'text-sm'">
			<span :class="[isDiscountedProduct() ? 'text-redcustom' : '']">
				{{ formattedPrice }}
			</span>
			<span v-if="isDiscountedProduct()" class="pl-2 line-through">
				{{ formattedOldPrice }}
			</span>
		</p>
		<p
			v-if="oldPrice && isDiscountedProduct() && showSaveText"
			class="text-sm text-redcustom"
		>
			{{ $t("Save") }} {{ formatPriceWithSymbol(oldPrice - price) }} ({{
				percent
			}}%)
		</p>
		<p
			v-if="oldPrice && showPrevious"
			class="text-sm text-gray-super-dark text-left"
		>
			{{ $t("Previously") }} {{ formattedOldPrice }}
		</p>
		<div
			v-if="isProductPage && historicalLowestPrice"
			class="text-xs text-left"
		>
			<Popover class="relative">
				<PopoverButton
					id="history-popover-button"
					class="flex items-center gap-1 outline-none text-gray-super-dark"
				>
					<InformationCircleIcon class="w-5 h-5 min-w-5" />
					{{ $t("View price history") }}
				</PopoverButton>
				<PopoverPanel
					id="history-popover-panel"
					class="absolute top-6 left-0 z-10 py-1 px-2 min-w-56 max-h-[480px] border bg-secondary"
				>
					{{
						$t("Lowest price in the last 30 days: {value}", {
							value: formatPriceWithSymbol(historicalLowestPrice)
						})
					}}
				</PopoverPanel>
			</Popover>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { currencyMap } from "@/utils/currencyConfig";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";

interface Props {
	price: number;
	oldPrice?: number | null;
	showSaveText?: boolean;
	showPrevious?: boolean;
	isProductPage?: boolean;
}

const {
	oldPrice,
	price,
	showSaveText,
	showPrevious = true
} = defineProps<Props>();

const norceStore = useNorceStore();
const { lowestPrice: historicalLowestPrice } = storeToRefs(useProductStore());

const currencySettings = computed(() => {
	const currencyCode = norceStore?.currentCurrency?.Code;
	return (
		currencyMap[currencyCode] || { symbol: currencyCode, position: "prefix" }
	);
});

const formatPriceWithSymbol = (price: number) => {
	const { symbol, position } = currencySettings.value;
	const formattedPrice = Number.isInteger(price) ? price : price.toFixed(2);
	return position === "prefix"
		? `${symbol} ${formattedPrice}`
		: `${formattedPrice} ${symbol}`;
};

const formattedPrice = computed(() => formatPriceWithSymbol(price));
const formattedOldPrice = computed(() =>
	oldPrice ? formatPriceWithSymbol(oldPrice) : ""
);

const percent = computed(() => {
	if (oldPrice && price) {
		const discount = Math.floor(((oldPrice - price) / oldPrice) * 100);

		if (discount) {
			return `-${discount}`;
		}
		return 0;
	}
	return null;
});

const isDiscountedProduct = () => oldPrice && oldPrice > price;
</script>
